import { AnyType } from '@src/models/common';
import { AppContext } from '@src/providers/App';
import { useCallback, useContext, useRef, useState } from 'react';
import { useEventCallback } from '../useEventCallback';
import { DEFAULT_ERROR_MESSAGE, SUCCESS_STATUS } from './consts';
import { IUseFetchParams } from './models';

/**
 * Хук для работы с api с помощью которого мы можем получить основные состояния для контроля отображения ui.
 *
 * Хук больше заточен под работу с `ng api` и для вывода сообщения об успеном запросе обрабатывает поле `status` у результата функции
 *
 * Функция `cb` может быть реализована как на основе `axios` так и нативного `fetch`
 *
 *
 *
 * @example
 *
 * const fetchClient = () => NG_API_CLIENT.get('client', {})
 *
 * const { loading, fetch, error, result } = useFetch<typeof fetchClient>({
 *  cb: fetchClient
 *  errorMessage: true
 * })
 */
export const useFetch = <F extends (...args: AnyType[]) => Promise<AnyType>>(params: IUseFetchParams<F> | F) => {
  const { defaultLoading = false } = params as IUseFetchParams<F>;
  const [loading, setLoading] = useState(defaultLoading);
  const [result, setResult] = useState<(ReturnType<F> extends Promise<infer U> ? U : ReturnType<F>) | null>(null);
  const [error, setError] = useState<AnyType | null>(null);
  const { setNotification } = useContext(AppContext);
  const returnObject = useRef({});

  const callback = useEventCallback(async (...args: Parameters<F>) => {
    setLoading(true);

    let successMessage: IUseFetchParams<F>['successMessage'] | null = null;
    let errorMessage: IUseFetchParams<F>['errorMessage'] | null = null;
    const cb = typeof params === 'object' ? params.cb : params;

    if (typeof params === 'object') {
      successMessage = params.successMessage;
      errorMessage = params.errorMessage;
    }

    try {
      const result = await cb(...args);
      if (result) {
        if (SUCCESS_STATUS.includes(result.status) && successMessage) {
          setNotification({
            view: 'success',
            ...successMessage,
          });
        }
      }

      setResult(result);
    } catch (e) {
      if (errorMessage) {
        const error = errorMessage === true ? DEFAULT_ERROR_MESSAGE : errorMessage;

        setNotification({
          view: 'error',
          ...error,
        });
      }

      setError(e);
    } finally {
      setLoading(false);
    }
  });

  const abort = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return Object.assign(returnObject.current, { fetch: callback, loading, error, result, abort }) as unknown as {
    fetch: F;
    loading: boolean;
    error: AnyType | null;
    result: (ReturnType<F> extends Promise<infer U> ? U : ReturnType<F>) | null;
    abort: () => void;
  };
};
